import { Accessor, createEffect, createMemo, createRoot, createSignal, Setter } from "solid-js";
import { createSharedSignal } from "./store";
import { Color } from "./Color";
import { debounce } from "./util";

export type Theme = 'default' | 'light' | 'dark' | 'dark-monochrome' | 'high-contrast'

const match = window.matchMedia('(prefers-color-scheme: dark)');
const [prefersDarkMode, setPrefersDarkMode] = createSignal(match.matches);
export { prefersDarkMode }

let theme: Accessor<Theme>;
let setTheme: Setter<Theme>;
createRoot(() => {
    [theme, setTheme] = createSharedSignal<Theme>('theme', () => prefersDarkMode() ? 'dark' : 'light')
})

export { theme, setTheme };

match.addEventListener('change', event => {
    setPrefersDarkMode(event.matches);
});

const bodyObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
        const themeAttr = document.body.getAttribute('data-color-theme');
        const currentTheme = theme();

        // Don't update signal if body shows resolved 'default' theme
        if (currentTheme === 'default') {
            const expectedResolved = prefersDarkMode() ? 'dark' : 'light';
            if (themeAttr === expectedResolved) {
                continue; // This is our internal resolution, not an external change
            }
        }

        if (mutation.attributeName === 'data-color-theme' && themeAttr !== currentTheme) {
            setTheme(themeAttr as Theme);
        }
    }
})
bodyObserver.observe(document.body, { attributes: true })

function highContrastColors(): Color[] {
    if (window.ST?.Preferences) {
        const colors: Color[] = (window.ST.Preferences.HighContrastColor?.split(',') ?? [])
            .filter((c: string) => !!c)
            .map((c: string) => new Color(c));
        if (!colors[0]) { colors.push(new Color('#fff')) };
        if (!colors[1]) { colors.push(colors[0].complement()); }

        return colors;
    }

    return [new Color('#fff'), new Color('#ccc')];
}

// SolidJS-side fallback for the legacy --hc-* variables that STX_ColorTheme.SetPageColor
// emits via <style id="STColorOverride">. When the full legacy framework (STContent.js)
// is loaded we leave it alone; in standalone SolidJS contexts (Storybook, stdev
// test_component, IAPlugins-only pages, future SolidJS-only pages) we emit our own
// <style id="SolidHCOverride"> so high-contrast still tracks the user's chosen color.
const SOLID_HC_STYLE_ID = 'SolidHCOverride';

// Detects the full legacy RT framework (STContent.js). We can't test for ST.Theme.SetPageColor
// because that exists in IAPlugins-only contexts where STX_ColorTheme is loaded but the rest
// of the legacy framework isn't. ST.View.UpdateScrollToTop is defined in STContent.js and
// is a reliable proxy for "the full legacy framework is hosting this page."
function legacyFrameworkPresent(): boolean {
    return typeof (window as Window & { ST?: { View?: { UpdateScrollToTop?: unknown } } })
        .ST?.View?.UpdateScrollToTop === 'function';
}

function clearSolidHcVars(): void {
    document.getElementById(SOLID_HC_STYLE_ID)?.remove();
}

function applySolidHcVars(): void {
    if (legacyFrameworkPresent()) {
        clearSolidHcVars();
        return;
    }

    const [bg, headerBg] = highContrastColors();
    const white = new Color('#fff');
    const black = new Color('#000');

    const luminanceContrast = (a: Color, b: Color): number => {
        const [hi, lo] = [a.luminosity(), b.luminosity()].sort((x, y) => y - x);
        return (hi + 0.05) / (lo + 0.05);
    };
    const maxContrast = (against: Color): Color =>
        luminanceContrast(white, against) >= luminanceContrast(black, against) ? white : black;

    const panelBg = bg.darken(10);
    const css = `:root {
    --hc-bgColor: ${bg.toString()};
    --hc-textColorView: ${maxContrast(bg).toString()};
    --hc-panelBGColor: ${panelBg.toString()};
    --hc-textColor: ${maxContrast(panelBg).toString()};
    --hc-panelHeading: ${headerBg.toString()};
    --hc-headingTextColor: ${maxContrast(headerBg).toString()};
    --hc-textColorLink: ${bg.complement().contrastTo(bg).toString()};
}`;

    let style = document.getElementById(SOLID_HC_STYLE_ID) as HTMLStyleElement | null;
    if (!style) {
        style = document.createElement('style');
        style.id = SOLID_HC_STYLE_ID;
        // Prepend so a later <style id="STColorOverride"> from STX_ColorTheme wins via cascade order
        document.head.insertBefore(style, document.head.firstChild);
    }
    style.textContent = css;
}

const postifx = () => {
    const currentTheme = theme();

    switch (currentTheme) {
        case 'dark':            return '_d';
        case 'dark-monochrome': return '_w';
        case 'light':           return '';
        case 'default':         return prefersDarkMode() ? '_d' : '';
        case 'high-contrast':
            const hcColors = highContrastColors();
            return (hcColors[0].luminosity() < 0.5) ? '_w' : '';
        default:
            // This is a compile-time check to ensure we handle all cases (ensures our switch is exhaustive)
            const _: never = currentTheme;
    }

    return '';
}

createRoot(() => {
    const updateHtmlTheme = debounce((theme: Theme) => {
        // We're using data-color-theme here to avoid any potential collision with the legacy theme attribute data-theme
        // When theme is default, resolve to light or dark based on OS preference
        const effectiveTheme = theme === 'default'
            ? (prefersDarkMode() ? 'dark' : 'light')
            : theme;
        document.body.setAttribute('data-color-theme', effectiveTheme)

        document.body.removeAttribute('data-hc-mode');
        if (theme == 'high-contrast') {
            const hcColors = highContrastColors();
            const hcMode = (hcColors[0].luminosity() < 0.5) ? 'dark' : 'light';
            document.body.setAttribute('data-hc-mode', hcMode);
            applySolidHcVars();
        } else {
            clearSolidHcVars();
        }

        // TODO: Update DX controls
    }, 100);

    if (import.meta.env.DEV) {
        if (localStorage['ST.ColorTheme']) {
            setTheme(localStorage['ST.ColorTheme']);
        }
    }

    createEffect(() => {
        const themeVal = theme();
        if (!document.body.classList.contains('ignore-theme')) {
            updateHtmlTheme(themeVal);
            localStorage['ST.ColorTheme'] = themeVal;
        }
    })

    // Re-evaluate hcMode whenever STX_ColorTheme.SetPageColor injects or replaces
    // <style id="STColorOverride">. The user can change their HC color without
    // changing the theme signal (still 'high-contrast'), and CSS variables update
    // reactively — but data-hc-mode is computed from luminosity at theme-change
    // time, so without this it goes stale and the wrong [data-hc-mode] bridge
    // branch keeps applying after a color swap.
    const colorOverrideObserver = new MutationObserver((mutations) => {
        for (const mut of mutations) {
            for (const node of [...mut.addedNodes, ...mut.removedNodes]) {
                if (node instanceof HTMLElement && node.id === 'STColorOverride') {
                    if (theme() === 'high-contrast') {
                        updateHtmlTheme(theme());
                    }
                    return;
                }
            }
        }
    });
    colorOverrideObserver.observe(document.head, { childList: true });
})

export function transformImagePath(path?: string | null) {
    if (!path) return '';

    const upath = path.toUpperCase();
    if (upath
        && upath.includes('DATA:IMAGE') === false
        && upath.startsWith('HTTP') === false
        && upath.startsWith('/') == false
        && upath.startsWith('PHOTOS') === false
        && upath.startsWith('IMAGES/') === false
        && upath.startsWith('GLYPH/') === false
    ) {
        path = `Images/${path}`;
    }

    if (upath.indexOf('ST_VIEW/') !== -1) {
        let qsIdx = path.indexOf('?');
        if (qsIdx === -1) {
            qsIdx = path.length;
        }

        const fnIdx = path.lastIndexOf('/', qsIdx);
        const extIdx = path.lastIndexOf('.', qsIdx);

        const dir = path.substring(0, fnIdx + 1);
        const filename = path.substring(fnIdx + 1, extIdx);
        const extension = path.substring(extIdx);
        const hasPostfix = filename.endsWith('_d') || filename.endsWith('_w');
        const basename = hasPostfix ? filename.substring(0, filename.length - 2) : filename;
        const postfix = postifx();

        path = dir + basename + postfix + extension;
    } else if (path) {
        path = path.replace(new RegExp('Images/AURORA', 'i'), 'Images/ST_AURORA');
        path = path.replace(new RegExp('Images/GENESEA', 'i'), 'Images/ST_GENESEA');
        path = path.replace(new RegExp('Images/GENESIS', 'i'), 'Images/ST_GENESIS');
        path = path.replace(new RegExp('Images/REVELATION', 'i'), 'Images/ST_REVELATION');
    }

    return path ?? '';
}

